import React from 'react';
import {
  Backdrop,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Formik, Form } from 'formik';
import type { PLRFormProps } from '../../../../../../types/preloan';
import { postCreateLoanRequestFromPLR } from '../../../../../../queries/preloan';
import ConfirmationDialog from './components/ConfirmationDialog';
import { FormSchemaV3 } from '../../../../../../types/preloan/v3';
import { postCheckout } from '../../../../../../queries/preloan/v3';
import { partnerErrorRedirectURL, preLoanRequestStep } from '../../formLogic';
import analytics from '../../../../../../utils/analytics';
import LoanProposalDetails, { productName } from '../../../../../../components/LoanProposalDetails';

export default function LoanConfirmationForm
<T extends FormSchemaV3>(props: PLRFormProps<T>) {
  const {
    formRef,
    formSchema,
    handleEnableLoading,
    handleStep,
    handleCurrentData,
  } = props;
  const [isTriggered, setIsTriggered] = React.useState(false);
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (formSchema.modifiedOffer) {
      analytics.page('PLR - RESULTADO', 'OFERTA MODIFICADA', {
        productType: formSchema.loanParams.productType,
        amount: formSchema.requestLoanData.amount,
        term: formSchema.requestLoanData.periods,
        rate: formSchema.requestLoanData.interestRate,
        installmentAmount: formSchema.requestLoanData.installment,
        insurances: formSchema.requestLoanData.checkedInsurances,
      });
    }
    handleEnableLoading(false);
  }, []);

  React.useEffect(() => {
    if (error) {
      const redirectURL = partnerErrorRedirectURL(
        formSchema.origin,
        formSchema.rut,
        formSchema.config,
      );
      if (redirectURL) {
        setTimeout(() => { window.location.href = redirectURL; }, 1000);
      }
    }
  }, [error]);

  const createLoanRequest = async () => {
    try {
      setOpenBackdrop(true);
      const lr = await postCreateLoanRequestFromPLR(
        formSchema.PLRtoken,
      );
      window.location.href = lr.data.url;
    } catch (e) {
      setError(true);
      setOpenBackdrop(false);
    }
  };

  React.useEffect(() => {
    if (!formSchema.modifiedOffer) {
      formRef.current?.handleSubmit();
    }
  }, []);

  return (
    <Formik
      innerRef={formRef}
      initialValues={formSchema}
      onSubmit={async () => {
        setError(false);
        setLoading(true);
        try {
          handleEnableLoading(true);
          const res = await postCheckout(formSchema.PLRtoken);
          handleCurrentData({ ...formSchema, step: res.data.plr.step });
          switch (res.data.plr.step) {
            case 'COMPANY_CONTACT_INFO':
              return handleStep(preLoanRequestStep.COMPANY_CONTACT_INFO);
            case 'LOAN_SIMULATION': {
              handleEnableLoading(false);
              setLoading(false);
              setIsTriggered(false);
              return setIsTriggered(true);
            }
            case 'MANUAL_VERIFICATION':
              return handleStep(preLoanRequestStep.MANUAL_VERIFICATION);
            case 'READY_FOR_LOAN':
              return handleStep(preLoanRequestStep.READY_FOR_LOAN);
            case 'COMPANY_APROVE':
              return handleStep(preLoanRequestStep.COMPANY_APPROVE);
            default:
              return setError(true);
          }
        } catch (e) {
          return setError(true);
        }
      }}
    >
      {({
        values,
      }) => (

        <Form>
          {!loading
            ? (
              <LoanProposalDetails
                title={`Este es el detalle del ${productName(values.loanParams.productType)} que solicitarás`}
                subtitle={formSchema.modifiedOffer ? '¡Por favor chequea que todo esté en orden!' : 'Descrito a continuación'}
                currency={values.loanParams.currency}
                loanData={values.requestLoanData}
                productType={values.loanParams.productType}
                dataPw="preloan-accepted-loan-title"
                highlight
              />
            )
            : (
              <>
                <br />
                <div>
                  <Typography>
                    Por favor espera un momento, esto podría tomar unos segundos...
                  </Typography>
                  <br />
                  <CircularProgress size={35} />
                </div>

              </>
            )}
          {error && (
          <Typography variant="caption" color="error">
            Ha ocurrido un error, por favor intente en otra ocasión o
            contáctenos
            al servicio al cliente.
          </Typography>
          )}
          <ConfirmationDialog
            formSchema={values}
            isTriggered={isTriggered}
            handleSubmit={createLoanRequest}
          />
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: (th) => th.zIndex.drawer + 1301,
            }}
            open={openBackdrop}
          >
            <div>
              <CircularProgress
                color="inherit"
              />
            </div>
            <b style={{ marginLeft: 20 }}>
              Un momento...
            </b>
          </Backdrop>
        </Form>
      )}

    </Formik>
  );
}
